import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { AlertTriangle, Bell, Mail, RefreshCw, Send, Webhook } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { AlertsChannelsConfig } from "./alerts-channels-config"
import { AlertsCooldownsConfig } from "./alerts-cooldowns-config"
import { AlertsLogTable } from "./alerts-log-table"
import { AlertsRoutingConfig } from "./alerts-routing-config"
import { AlertsTestDialog } from "./alerts-test-dialog"
import {
  useAdminAlertConfigQuery,
  useRawEntityQuery,
  useSaveRawEntityMutation,
  useUpdateAlertConfigMutation,
} from "../../hooks/admin-queries"
import { CollapsibleManagementSection } from "../collapsible-management-section"
import { DelegationModeControl } from "../delegation-mode-control"
import { RawJsonTab } from "../shared/raw-json-tab"

export function AlertsConfigPanel() {
  const { t } = useTranslation()
  const [testDialogOpen, setTestDialogOpen] = useState(false)

  const { data: config, isLoading, refetch } = useAdminAlertConfigQuery()
  const updateMutation = useUpdateAlertConfigMutation()

  // Raw JSON tab plumbing (singleton endpoint).
  const rawEndpoint = "/admin/alerts/config/raw"
  const { data: rawData, refetch: refetchRaw } =
    useRawEntityQuery<Record<string, unknown>>(rawEndpoint)
  const rawSaveMutation = useSaveRawEntityMutation<Record<string, unknown>>({
    endpoint: rawEndpoint,
    invalidateOnSuccess: [["admin", "alerts"]],
  })
  const rawStored = useMemo(() => rawData?.entity ?? {}, [rawData])
  const rawHash = rawData?.hash ?? ""
  const [rawResetSignal, setRawResetSignal] = useState(0)
  useEffect(() => {
    setRawResetSignal((n) => n + 1)
  }, [rawStored])

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!rawHash) {
      // Without a fresh hash we'd be saving against an unknown baseline,
      // and silently returning would close the confirm dialog as if the
      // save succeeded. Throw so the dialog surfaces the failure and the
      // admin can retry once the raw fetch settles.
      throw new Error(
        t("adminDashboard.alertsEdit.errorRawNotLoaded", {
          defaultValue: "Raw config not loaded yet — please retry",
        })
      )
    }
    const parsed = (next || {}) as Record<string, unknown>
    try {
      await rawSaveMutation.mutateAsync({
        entity: parsed,
        expectedHash: rawHash,
        reason,
        source: "raw",
      })
      void refetchRaw()
      void refetch()
      toast.success(
        t("adminDashboard.alertsEdit.toastSavedRaw", { defaultValue: "Alert config saved (raw)" })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.alertsEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  const handleToggleEnabled = async (enabled: boolean) => {
    await updateMutation.mutateAsync({ enabled })
  }

  const headerActions = (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" onClick={() => refetch()}>
        <RefreshCw className="mr-2 size-4" />
        {t("common.refresh", "Refresh")}
      </Button>
      <Button size="sm" variant="default" onClick={() => setTestDialogOpen(true)}>
        <Send className="mr-2 size-4" />
        {t("adminDashboard.alerts.sendTest", "Send Test Alert")}
      </Button>
      <DelegationModeControl capability="alerts" />
    </div>
  )

  if (isLoading) {
    return (
      <CollapsibleManagementSection
        headerActions={headerActions}
        icon={<Bell className="size-5" />}
        id="alerts"
        storageKey="admin-management-alerts-expanded"
        title={t("adminDashboard.alerts.title", "Alert Configuration")}
      >
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="text-muted-foreground size-6 animate-spin" />
        </div>
      </CollapsibleManagementSection>
    )
  }

  if (!config) {
    return (
      <CollapsibleManagementSection
        headerActions={headerActions}
        icon={<Bell className="size-5" />}
        id="alerts"
        storageKey="admin-management-alerts-expanded"
        title={t("adminDashboard.alerts.title", "Alert Configuration")}
      >
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
          <AlertTriangle className="size-5" />
          {t("adminDashboard.alerts.notConfigured", "Alert system not configured")}
        </div>
      </CollapsibleManagementSection>
    )
  }

  return (
    <CollapsibleManagementSection
      headerActions={headerActions}
      icon={<Bell className="size-5" />}
      id="alerts"
      storageKey="admin-management-alerts-expanded"
      title={t("adminDashboard.alerts.title", "Alert Configuration")}
    >
      <Tabs defaultValue="form">
        <TabsList>
          <TabsTrigger value="form">
            {t("adminDashboard.alertsEdit.tabForm", { defaultValue: "Form" })}
          </TabsTrigger>
          <TabsTrigger
            className="text-destructive data-[state=active]:text-destructive"
            value="raw"
          >
            {t("adminDashboard.alertsEdit.tabRaw", { defaultValue: "Raw JSON" })}
          </TabsTrigger>
        </TabsList>
        <TabsContent className="mt-4" value="form">
          <div className="space-y-6">
            {/* Global toggles */}
            <div className="bg-muted/50 flex flex-wrap items-center gap-6 rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={config.enabled}
                  disabled={updateMutation.isPending}
                  aria-label={t("adminDashboard.alerts.enabledControl", {
                    defaultValue: "Alerts enabled",
                  })}
                  onCheckedChange={handleToggleEnabled}
                />
                <div>
                  <div className="font-medium">
                    {t("adminDashboard.alerts.enabled", "Alerts Enabled")}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {config.enabled
                      ? t(
                          "adminDashboard.alerts.enabledDesc",
                          "Alerts will be sent to configured channels"
                        )
                      : t("adminDashboard.alerts.disabledDesc", "All alerts are disabled")}
                  </div>
                </div>
              </div>

              {config.dry_run && (
                <>
                  <div className="bg-border h-8 w-px" />

                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        {t("adminDashboard.alerts.dryRun", "Dry Run Mode")}
                        <Badge variant="destructive">
                          {t("adminDashboard.alerts.active", "Active")}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground text-sm">
                        {t(
                          "adminDashboard.alerts.dryRunEnvDesc",
                          "Set via ALERT_DRY_RUN environment variable"
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="bg-border h-8 w-px" />

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="text-muted-foreground size-4" />
                  <span className="text-sm">
                    {config.channels.email.enabled
                      ? t("adminDashboard.alerts.emailEnabled", "Email enabled")
                      : t("adminDashboard.alerts.emailDisabled", "Email disabled")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Webhook className="text-muted-foreground size-4" />
                  <span className="text-sm">
                    {config.channels.webhook.enabled
                      ? t("adminDashboard.alerts.webhookEnabled", "Webhook enabled")
                      : t("adminDashboard.alerts.webhookDisabled", "Webhook disabled")}
                  </span>
                </div>
              </div>
            </div>

            {/* Tabs for different config sections */}
            <Tabs defaultValue="channels">
              <TabsList>
                <TabsTrigger value="channels">
                  {t("adminDashboard.alerts.channels", "Channels")}
                </TabsTrigger>
                <TabsTrigger value="routing">
                  {t("adminDashboard.alerts.routing", "Routing")}
                </TabsTrigger>
                <TabsTrigger value="cooldowns">
                  {t("adminDashboard.alerts.cooldowns", "Cooldowns")}
                </TabsTrigger>
                <TabsTrigger value="logs">
                  {t("adminDashboard.alerts.logs", "Alert History")}
                </TabsTrigger>
              </TabsList>

              <TabsContent className="mt-4" value="channels">
                <AlertsChannelsConfig config={config} />
              </TabsContent>

              <TabsContent className="mt-4" value="routing">
                <AlertsRoutingConfig config={config} />
              </TabsContent>

              <TabsContent className="mt-4" value="cooldowns">
                <AlertsCooldownsConfig config={config} />
              </TabsContent>

              <TabsContent className="mt-4" value="logs">
                <AlertsLogTable />
              </TabsContent>
            </Tabs>

            <AlertsTestDialog open={testDialogOpen} onOpenChange={setTestDialogOpen} />
          </div>
        </TabsContent>
        <TabsContent className="mt-4" value="raw">
          <RawJsonTab
            confirmToken="alerts"
            isPending={rawSaveMutation.isPending || !rawHash}
            resetSignal={rawResetSignal}
            value={rawStored}
            resourceLabel={t("adminDashboard.alertsEdit.rawResourceLabel", {
              defaultValue: "alert config",
            })}
            onSave={handleRawSave}
          />
        </TabsContent>
      </Tabs>
    </CollapsibleManagementSection>
  )
}
